import { useEffect, useMemo, useState } from "react";
import { Inbox, Mail, Building2, Phone, Loader2, Search } from "lucide-react";
import api from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];
const STATUS_STYLE = {
  new: "border-gold-500/50 text-gold-300",
  contacted: "border-sky-400/50 text-sky-300",
  qualified: "border-violet-400/50 text-violet-300",
  won: "border-emerald-400/50 text-emerald-300",
  lost: "border-white/25 text-white/40",
};

export default function LeadsInbox() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/leads").then((r) => setLeads(r.data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    await api.patch(`/leads/${id}`, { status }).catch(() => load());
  };

  const counts = useMemo(() => {
    const c = { all: leads.length };
    STATUSES.forEach((s) => (c[s] = leads.filter((l) => (l.status || "new") === s).length));
    return c;
  }, [leads]);

  const shown = leads.filter((l) => (filter === "all" || (l.status || "new") === filter))
    .filter((l) => !q || `${l.name} ${l.email} ${l.company} ${l.message}`.toLowerCase().includes(q.toLowerCase()));

  if (denied) return <div className="text-white/60" data-testid="leads-denied">Admins only.</div>;

  return (
    <div className="space-y-6 text-white" data-testid="leads-page">
      <div>
        <Mono className="text-gold-400 text-[0.65rem]">Growth · demo.inbox</Mono>
        <h1 className="font-heading font-light text-4xl tracking-tight mt-3 flex items-center gap-3"><Inbox className="h-8 w-8 text-gold-500/70" /> Leads</h1>
        <p className="text-white/55 mt-3 font-light">Triage "Book a demo" enquiries from the marketing site.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="leads-filters">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className={`px-3 py-2 text-xs border transition-colors ${filter === s ? "border-gold-500/60 bg-navy-900 text-white" : "border-gold-500/15 text-white/45 hover:text-white/70"}`}>
            <span className="capitalize">{s}</span> <span className="font-mono text-white/40">· {counts[s] || 0}</span>
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads" data-testid="leads-search" className="bg-navy-950 border border-gold-500/20 pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500 w-52" />
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex items-center justify-center text-white/50"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : shown.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 p-16 text-center text-white/45 font-light" data-testid="leads-empty">No leads to show.</div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {shown.map((l) => (
            <div key={l.id} className="border border-gold-500/20 bg-navy-900/40 p-5" data-testid={`lead-${l.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading text-lg">{l.name}</h3>
                    {l.interest && <span className="font-mono text-[0.5rem] uppercase tracking-wider px-2 py-0.5 border border-gold-500/30 text-gold-300">{l.interest}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-white/55 text-sm">
                    <a href={`mailto:${l.email}`} className="flex items-center gap-1.5 hover:text-gold-300"><Mail className="h-3.5 w-3.5" /> {l.email}</a>
                    {l.company && <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-gold-500/60" /> {l.company}</span>}
                    {l.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-gold-500/60" /> {l.phone}</span>}
                  </div>
                </div>
                <select value={l.status || "new"} onChange={(e) => setStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`}
                  className={`bg-navy-950 border px-3 py-2 text-xs font-mono uppercase tracking-wider focus:outline-none cursor-pointer ${STATUS_STYLE[l.status || "new"]}`}>
                  {STATUSES.map((s) => <option key={s} value={s} className="bg-navy-900 text-white">{s}</option>)}
                </select>
              </div>
              {l.message && <p className="text-white/70 text-sm mt-3 border-l-2 border-gold-500/40 pl-3 font-light">{l.message}</p>}
              {l.created_at && <Mono className="text-[0.5rem] text-white/30 mt-3 block">{new Date(l.created_at).toLocaleString("en-AU")}</Mono>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
